import { Component, ChangeDetectionStrategy, ViewChild, TemplateRef, Input } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { ArticleNotification, Message } from "../../../../core/models/message.model";
import { ArticleListComponent, ArticleDisplaySection, ArticleDisplayItem } from "../article-list/article-list.component";

@Component({
  selector: "app-article-expansion-panel",
  standalone: true,
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatCardModule,
    MatChipsModule,
    ArticleListComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    ::ng-deep .mat-mdc-dialog-container {
      height: 90vh !important;
      max-height: 90vh !important;
    }

    ::ng-deep .mat-mdc-dialog-surface {
      height: 100% !important;
      max-height: none !important;
      display: flex !important;
      flex-direction: column !important;
    }

    ::ng-deep .mat-mdc-dialog-content {
      flex: 1 !important;
      height: 100% !important;
      max-height: none !important;
      display: flex !important;
      flex-direction: column !important;
      overflow: hidden !important;
    }

    ::ng-deep .full-height-dialog .mat-mdc-dialog-container {
      height: 95vh !important;
      max-height: 95vh !important;
    }

    .compact-scroll {
      scrollbar-width: thin;
      scrollbar-color: #cbd5e1 transparent;
    }
    .compact-scroll::-webkit-scrollbar        { width: 3px; }
    .compact-scroll::-webkit-scrollbar-track  { background: transparent; }
    .compact-scroll::-webkit-scrollbar-thumb  { background: #cbd5e1; border-radius: 2px; }
    .compact-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
    .dark .compact-scroll { scrollbar-color: #4b5563 transparent; }
    .dark .compact-scroll::-webkit-scrollbar-thumb       { background: #4b5563; }
    .dark .compact-scroll::-webkit-scrollbar-thumb:hover { background: #6b7280; }

    .info-card {
      height: 35vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .info-card .mat-mdc-card-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    .info-section {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }
    .articles-section {
      min-height: 0;
      flex: 1;
    }
    .articles-container {
      padding-bottom: 2rem;
    }
  `],
  template: `
    <ng-template #dialogTemplate>
      <mat-dialog-content class="p-3 flex flex-col h-full">

        <!-- Info Card -->
        <mat-card class="info-card flex-shrink-0">
          <mat-card-content class="p-2 text-black dark:text-white">
            <div class="grid md:grid-cols-2 gap-2 text-xs flex-1 min-h-0">
              <div class="flex flex-col min-h-0">
                <label class="font-medium mb-1 block flex-shrink-0">
                  Preguntas generadas ({{ getRetrievedArticlesNotifications().length }}):
                </label>
                <div class="info-section compact-scroll space-y-1">
                  @for (notification of getRetrievedArticlesNotifications(); track notification; let i = $index) {
                    <div class="bg-blue-50 dark:bg-blue-900/20 p-1 rounded">
                      <strong>{{ i + 1 }}:</strong> {{ notification.userQuestion }}
                    </div>
                  }
                </div>
              </div>
            </div>
            <div class="mt-2 text-xs flex flex-col flex-1 min-h-0">
              <label class="font-medium mb-1 block flex-shrink-0">Respuesta del Bot:</label>
              <div class="bg-green-50 dark:bg-green-900/20 p-2 rounded border-l-2 border-green-500 info-section compact-scroll"
                [innerHTML]="formatTextWithLineBreaks(getBotMessageContent(messageId))">
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Articles Section -->
        <div class="articles-section flex flex-col mt-2">
          <h3 class="text-sm font-semibold text-black dark:text-white mb-2 flex-shrink-0">
            Artículos Recuperados ({{ getTotalRetrievedArticlesCount() }})
            @if (getTotalDiscardedArticlesCount() > 0) {
              <span class="text-red-500 ml-2">| Descartados ({{ getTotalDiscardedArticlesCount() }})</span>
            }
          </h3>
          <div class="flex-1 overflow-y-auto compact-scroll">
            <div class="articles-container">
              @for (notification of getRetrievedArticlesNotifications(); track notification; let ni = $index) {
                <app-article-list [sections]="toSections(notification, ni)"></app-article-list>
              }
            </div>
          </div>
        </div>

      </mat-dialog-content>
    </ng-template>
  `,
})
export class ArticleExpansionPanelComponent {
  @ViewChild("dialogTemplate") dialogTemplate!: TemplateRef<any>;

  @Input() retrievedArticles?: ArticleNotification[] | null;
  @Input() messages: Message[] | null = [];
  @Input() messageId?: string;

  constructor(private dialog: MatDialog) {}

  openDialog(): void {
    this.dialog.open(this.dialogTemplate, {
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      maxHeight: '95vh',
      panelClass: 'full-height-dialog',
    });
  }

  toSections(notification: ArticleNotification, index: number): ArticleDisplaySection[] {
    const mapArticle = (a: ArticleNotification['articles'][number]): ArticleDisplayItem => ({
      identifier: a.identifier,
      score: a.similarityScore,
      category: a.category1,
      subCategory: a.category2,
      question: a.documentQuestion,
      content: a.documentContent,
      action: a.action,
      requestKey: a.requestKey,
    });

    const sections: ArticleDisplaySection[] = [
      {
        label: `Notificación ${index + 1}`,
        style: 'blue',
        articles: (notification.articles ?? []).map(mapArticle),
        proposedAction: notification.proposedAction,
      },
    ];

    if (notification.discardedArticles?.length) {
      sections.push({
        label: 'Artículos Descartados',
        style: 'red',
        articles: notification.discardedArticles.map(mapArticle),
      });
    }

    return sections;
  }

  formatTextWithLineBreaks(text: string | undefined): string {
    if (!text) return 'N/A';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
  }

  getBotMessageContent(answeredMessageId: string | undefined): string {
    if (!answeredMessageId || !this.messages) return 'N/A';
    const botMessage = this.messages.find(msg => msg.id === answeredMessageId && !msg.isUser);
    return botMessage?.content || 'Respuesta del bot no encontrada';
  }

  getRetrievedArticlesNotifications(): ArticleNotification[] {
    return this.retrievedArticles ?? [];
  }

  getTotalRetrievedArticlesCount(): number {
    return this.getRetrievedArticlesNotifications()
      .reduce((total, n) => total + (n.articles?.length ?? 0), 0);
  }

  getTotalDiscardedArticlesCount(): number {
    return this.getRetrievedArticlesNotifications()
      .reduce((total, n) => total + (n.discardedArticles?.length ?? 0), 0);
  }
}
